/**
 * Résultats jalons atelier : impression / thermo / découpe, mode laser-manuel.
 */

export const PRINTING_OUTCOME_OPTIONS = [
  {
    value: 'complet',
    label: 'Complète',
    tone: 'success',
    segmentClass:
      'bg-green-600 text-white border-green-600 hover:bg-green-700',
    badgeClass:
      'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30',
    icon: 'CheckCircle2',
  },
  {
    value: 'partiel',
    label: 'Partiel',
    tone: 'warning',
    segmentClass:
      'bg-amber-500 text-white border-amber-500 hover:bg-amber-600',
    badgeClass:
      'bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30',
    icon: 'CircleDashed',
  },
  {
    value: 'echec',
    label: 'Échec',
    tone: 'destructive',
    segmentClass:
      'bg-red-600 text-white border-red-600 hover:bg-red-700',
    badgeClass:
      'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30',
    icon: 'XCircle',
  },
];

export const CUT_MODE_OPTIONS = [
  {
    value: 'laser',
    label: 'Laser',
    segmentClass:
      'bg-primary text-primary-foreground border-primary hover:bg-primary/90',
    badgeClass: 'bg-primary/15 text-primary border border-primary/30',
    icon: 'Zap',
  },
  {
    value: 'manuel',
    label: 'Manuel',
    segmentClass:
      'bg-slate-600 text-white border-slate-600 hover:bg-slate-700',
    badgeClass:
      'bg-slate-500/15 text-slate-800 dark:text-slate-300 border border-slate-500/30',
    icon: 'Scissors',
  },
];

export const MILESTONE_INTERACTION = {
  SIMPLE: 'simple',
  OUTCOME: 'outcome',
  CUT_MODE: 'cut_mode',
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export function getWorkshopPhaseKind(phaseName) {
  const n = normalize(phaseName);
  if (n.includes('thermo')) return 'thermo';
  if (
    n.includes('finition') ||
    n.includes('decoupe') ||
    n.includes('découpe') ||
    (n.includes('decoupe') && n.includes('finition'))
  ) {
    return 'finishing';
  }
  if (
    n.includes('impression') ||
    n.includes('3d') ||
    (n.includes('production') && !n.includes('pre'))
  ) {
    return 'production_3d';
  }
  return 'other';
}

/** Jalon « issue impression » par libellé (repli). */
export function isPrintingOutcomeMilestone(name) {
  const n = normalize(name);
  if (!n.includes('impression')) return false;
  return (
    n.includes('complet') ||
    n.includes('echec') ||
    n.includes('partiel') ||
    (n.includes('total') && n.includes('partiel'))
  );
}

export function isPrintingFinishedMilestone(name) {
  const n = normalize(name);
  return (
    n.includes('impression') &&
    (n.includes('termine') || n.includes('finished'))
  );
}

/**
 * Type d’interaction atelier pour un jalon (ordre + phase).
 * @param {object} milestone
 * @param {{ name?: string } | null} phase
 */
export function getMilestoneInteractionKind(milestone, phase) {
  const order = milestone?.milestone_order ?? 0;
  const phaseKind = getWorkshopPhaseKind(phase?.name);
  const n = normalize(milestone?.name || milestone?.milestone_name);

  if (phaseKind === 'finishing' && order === 1) {
    return MILESTONE_INTERACTION.CUT_MODE;
  }
  if (
    phaseKind === 'finishing' &&
    order === 2 &&
    (n.includes('etat') || n.includes('état'))
  ) {
    return MILESTONE_INTERACTION.OUTCOME;
  }
  if (phaseKind === 'production_3d' && order === 2) {
    return MILESTONE_INTERACTION.OUTCOME;
  }
  if (phaseKind === 'thermo' && order === 2) {
    return MILESTONE_INTERACTION.OUTCOME;
  }

  if (isPrintingOutcomeMilestone(milestone?.name)) {
    return MILESTONE_INTERACTION.OUTCOME;
  }
  if (
    (n.includes('etat') || n.includes('état')) &&
    (n.includes('decoupe') || n.includes('découpe'))
  ) {
    return MILESTONE_INTERACTION.OUTCOME;
  }
  if (n.includes('laser') && n.includes('manuel')) {
    return MILESTONE_INTERACTION.CUT_MODE;
  }

  return MILESTONE_INTERACTION.SIMPLE;
}

export function getOutcomeOption(completionType) {
  if (!completionType) return null;
  return (
    PRINTING_OUTCOME_OPTIONS.find((o) => o.value === completionType) ||
    CUT_MODE_OPTIONS.find((o) => o.value === completionType) ||
    null
  );
}

export function getOutcomeDisplay(completionType) {
  const opt = getOutcomeOption(completionType);
  if (!opt) return null;
  return {
    label: opt.label,
    badgeClass: opt.badgeClass,
    icon: opt.icon,
    value: opt.value,
  };
}

export function getOutcomeResultMessage(completionType, outcomeQuantity) {
  switch (completionType) {
    case 'complet':
      return 'Complète';
    case 'partiel':
      return 'Partiel';
    case 'echec': {
      const q = outcomeQuantity != null ? Number(outcomeQuantity) : null;
      if (q != null && !Number.isNaN(q) && q > 0) {
        return `Échec — ${q} aligneur${q > 1 ? 's' : ''}`;
      }
      return 'Échec';
    }
    case 'laser':
      return 'Mode Laser';
    case 'manuel':
      return 'Mode Manuel';
    default:
      return null;
  }
}

export function getMilestoneResultSummary(milestone) {
  const type = milestone?.completion_type ?? milestone?.completionType;
  const qty = milestone?.outcome_quantity ?? milestone?.outcomeQuantity;
  const fromOutcome = getOutcomeResultMessage(type, qty);
  if (fromOutcome) return fromOutcome;

  const name = milestone?.name || milestone?.milestone_name || '';
  if (isPrintingFinishedMilestone(name) && type) {
    return getOutcomeResultMessage(type, qty);
  }

  const desc = milestone?.description;
  if (desc && normalize(desc).includes('achev') && normalize(desc).includes('succ')) {
    return null;
  }
  return desc || null;
}

/** Sous-titre affiché sur le bouton jalon (liste atelier). */
export function getMilestoneDisplaySubtitle(milestone, phaseStatus) {
  const isCompleted = milestone?.completed === true;
  const isRejected =
    milestone?.is_rejected === true || milestone?.rejected_date != null;

  if (phaseStatus === 'in_progress' && isRejected && !isCompleted) {
    return 'Problème signalé';
  }

  const result = getMilestoneResultSummary(milestone);
  if (isCompleted && result) return result;

  if (isCompleted && milestone?.updated_at) {
    return `Validé le ${new Date(milestone.updated_at).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }
  if (isRejected && milestone?.note) {
    const short =
      milestone.note.length > 48 ? `${milestone.note.slice(0, 48)}…` : milestone.note;
    return `Rejet : ${short}`;
  }
  if (isRejected) return 'Problème signalé';
  if (isCompleted) return 'Validé';
  return 'En attente';
}

export function getOutcomeValidationToast(completionType, outcomeQuantity) {
  const msg = getOutcomeResultMessage(completionType, outcomeQuantity);
  return msg ? `Jalon validé — ${msg}` : 'Jalon validé';
}

export function resolvePrintingOutcomeForPhase(milestones, currentMilestone) {
  if (currentMilestone?.completion_type) return currentMilestone.completion_type;
  const source = (milestones || []).find(
    (m) =>
      isPrintingOutcomeMilestone(m?.name || m?.milestone_name) && m?.completion_type
  );
  return source?.completion_type || null;
}

export function buildMilestoneMetaFromPayload(milestone, payload, userId, phase) {
  const meta = { validated_by_user_id: userId || null };
  const kind = getMilestoneInteractionKind(milestone, phase);

  if (payload?.note?.trim()) {
    meta.note = payload.note.trim();
  }

  if (kind === MILESTONE_INTERACTION.OUTCOME) {
    if (payload?.outcomeChoice) {
      meta.completion_type = payload.outcomeChoice;
      if (payload.outcomeChoice === 'echec') {
        const q = Number(payload.failedAligners);
        if (!Number.isNaN(q) && q >= 0) meta.outcome_quantity = Math.floor(q);
      }
    }
  } else if (kind === MILESTONE_INTERACTION.CUT_MODE) {
    if (payload?.modeChoice === 'laser' || payload?.modeChoice === 'manuel') {
      meta.completion_type = payload.modeChoice;
      meta.milestone_name =
        payload.modeChoice === 'laser' ? 'Laser' : 'Manuel';
    }
  } else if (payload?.outcomeChoice && ['complet', 'partiel', 'echec'].includes(payload.outcomeChoice)) {
    meta.completion_type = payload.outcomeChoice;
  }

  if (payload?.nameChoice?.trim()) {
    meta.milestone_name = payload.nameChoice.trim();
  }

  return meta;
}

export function buildRejectMilestoneMeta(rejectNote, userId) {
  return {
    note: String(rejectNote || '').trim(),
    validated_by_user_id: userId || null,
  };
}
